import {
  DEFAULT_SIZE,
  DOESNT_HAVE_THE_PHRASE,
  DOESNT_HAVE_THE_WORDS,
  END_DATE,
  FILTER_PARAM,
  GALLERY_SIZE,
  GRID,
  HAS_THE_PHRASE,
  HAS_THE_WORDS,
  INCLUDE_TRANSCRIPT,
  LAYOUT,
  PAGE,
  SEARCH_PARAM,
  SIZE,
  SORT,
  START,
  START_DATE,
  VIEW_CONTEXT,
} from 'src/constants';
import { clearFilters } from 'src/helpers/filterPersistence';
import { CustomURLSearchParams } from 'src/store/helpers';
import viewStateVar from 'src/store/viewState';

/**
 * Filter chip structure
 */
export interface FilterChip {
  category: string;
  choice: string;
}

/**
 * Reset filter arguments for URL creation
 */
interface ResetFilterArgs {
  [key: string]: string;
}

/**
 * Options for filter reset
 */
interface FilterResetOptions {
  size?: number;
}

/**
 * Options for gallery scope reset
 */
interface GalleryScopeResetOptions {
  size?: number;
  preserveSort?: boolean;
  galleryId?: string | null;
}

/**
 * View context scope type
 */
type ViewContextScope = typeof VIEW_CONTEXT.LIBRARY | typeof VIEW_CONTEXT.GALLERY;

/**
 * Creates a new CustomURLSearchParams instance
 *
 * @param resetFilterArgs - Optional initial arguments
 * @returns CustomURLSearchParams instance
 */
export const getCustomSearchURL = (resetFilterArgs?: ResetFilterArgs): CustomURLSearchParams => {
  const params = new CustomURLSearchParams();
  if (resetFilterArgs) {
    Object.entries(resetFilterArgs).forEach(([key, value]) => {
      params.set(key, value);
    });
  }
  return params;
};

/**
 * Clears all search parameters from the URL
 */
export const clearSearchParams = (): void => {
  const customSearchURL = getCustomSearchURL();

  customSearchURL.forEach((_value, key) => {
    customSearchURL.delete(key);
  });
};

/**
 * Sets filter parameters in the URL from filter chips
 *
 * @param chips - Array of filter chips to set
 */
export const setFilterParams = (chips: FilterChip[]): void => {
  const customSearchURL = getCustomSearchURL();
  if (chips.length) {
    const filters = chips.map((chip) => {
      return `${chip.category}:${chip.choice}`;
    });

    for (const chip of filters) {
      customSearchURL.append(FILTER_PARAM, chip);
    }
  } else {
    customSearchURL.delete(FILTER_PARAM);
  }
  customSearchURL.set(START, '0');
  customSearchURL.set(PAGE, '1');
};

/**
 * Creates a hook function that resets sort data in view state
 *
 * @returns Function to reset sort data
 */
export const useSortRest = (): (() => void) => {
  return () => {
    viewStateVar({
      ...viewStateVar(),
      sortData: {
        sortBy: { value: '', fieldType: '' },
        orderBy: 'desc',
        field: null,
      },
    });
  };
};

/**
 * Initial navigation params for TanStack Router.
 * Used when navigating to a gallery/collection.
 */
export interface InitialNavParams {
  to: string;
  search: {
    size: number;
    page: number;
    layout: 'grid' | 'inline' | 'twoup' | 'gallery_only';
    sort?: string;
  };
}

/**
 * Creates a hook function that generates initial navigation parameters for TanStack Router.
 * Returns separate `to` (path) and `search` (params) for proper typed navigation.
 *
 * IMPORTANT: Do NOT pass string URLs with query params to TanStack Router's Link component.
 * Always use the `to` prop for the path and `search` prop for query parameters.
 *
 * @param size - Page size (defaults to DEFAULT_SIZE)
 * @returns Function that takes a path and returns { to, search } for TanStack Router
 */
export const useInitialNavParams = (size: number = DEFAULT_SIZE): ((path: string) => InitialNavParams) => {
  const { sortData } = viewStateVar();

  // Build sort value if available
  const sortValue = sortData?.sortBy?.value ? `${sortData.sortBy.value}:${sortData.orderBy || 'desc'}` : undefined;

  return (path: string): InitialNavParams => ({
    to: path,
    search: {
      size,
      page: 1,
      layout: GRID as 'grid',
      ...(sortValue && { sort: sortValue }),
    },
  });
};

/**
 * @deprecated Use useInitialNavParams instead for TanStack Router compatibility.
 * This function returns string URLs with embedded query params which can cause
 * URL corruption when used with TanStack Router's Link component.
 *
 * Creates a hook function that generates initial URL parameters
 *
 * @param size - Page size (defaults to DEFAULT_SIZE)
 * @returns Function that takes a path and returns URL with params
 */
export const useInitialUrlParams = (size: number = DEFAULT_SIZE): ((path: string) => string) => {
  const { sortData } = viewStateVar();
  const urlParams = new URLSearchParams();
  urlParams.set(SIZE, String(size));
  // NOTE: 'start' is calculated from page and size by the backend/query processor
  // Do not include it in URL params - it causes schema validation issues
  urlParams.set(PAGE, '1');
  urlParams.set(LAYOUT, GRID);
  if (sortData?.sortBy?.value) {
    urlParams.set(SORT, `${sortData.sortBy.value}:${sortData.orderBy || 'desc'}`);
  }

  return (path: string): string => {
    return `${path}?${urlParams.toString()}`;
  };
};

/**
 * Creates a hook function that resets all filters
 *
 * @returns Function to reset filters
 */
export const useFilterReset = (): ((options?: FilterResetOptions) => void) => {
  const customSearchURL = getCustomSearchURL();

  return ({ size = DEFAULT_SIZE }: FilterResetOptions = {}): void => {
    customSearchURL.delete(FILTER_PARAM);
    customSearchURL.delete(SEARCH_PARAM);
    customSearchURL.delete(HAS_THE_WORDS);
    customSearchURL.delete(DOESNT_HAVE_THE_WORDS);
    customSearchURL.delete(HAS_THE_PHRASE);
    customSearchURL.delete(DOESNT_HAVE_THE_PHRASE);
    customSearchURL.delete(START_DATE);
    customSearchURL.delete(END_DATE);
    customSearchURL.delete(INCLUDE_TRANSCRIPT);
    customSearchURL.delete(PAGE);
    customSearchURL.delete(SIZE);

    customSearchURL.set(SIZE, String(size));

    // Clear persisted filters for library scope (persist the "cleared" state)
    clearFilters(VIEW_CONTEXT.LIBRARY as ViewContextScope);

    viewStateVar({
      ...viewStateVar(),
      regularStartDate: '',
      regularEndDate: '',
      ISOStartDate: '',
      ISOEndDate: '',
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      showHorizontal: false,
      filterChips: [],
      filterCategoryExclude: [],
      pagination: {
        ...viewStateVar().pagination,
        page: 1,
        cursor: null,
      },
      filter: {
        category: { id: '', name: '', label: '' },
        choices: [],
      },
      sortData: {
        sortBy: { value: '', fieldType: '' },
        orderBy: 'desc',
        field: null,
      },
    });
  };
};

/**
 * Reset Gallery scope: clear filters/search-related params and state, reset pagination and size, and preserve sort when available.
 * Does NOT reload the page.
 *
 * @param options - Reset options including size, preserveSort, and galleryId
 */
export const resetGalleryScope = ({
  size = GALLERY_SIZE,
  preserveSort = true,
  galleryId = null,
}: GalleryScopeResetOptions = {}): void => {
  const customSearchURL = getCustomSearchURL();

  // Capture existing sort before we clear params
  const existingSort = customSearchURL.get(SORT);

  // Clear search/filter params
  customSearchURL.delete(FILTER_PARAM);
  customSearchURL.delete(SEARCH_PARAM);
  customSearchURL.delete(HAS_THE_WORDS);
  customSearchURL.delete(DOESNT_HAVE_THE_WORDS);
  customSearchURL.delete(HAS_THE_PHRASE);
  customSearchURL.delete(DOESNT_HAVE_THE_PHRASE);
  customSearchURL.delete(START_DATE);
  customSearchURL.delete(END_DATE);
  customSearchURL.delete(INCLUDE_TRANSCRIPT);

  // Reset pagination and size
  customSearchURL.delete(PAGE);
  customSearchURL.delete(START);
  customSearchURL.delete(SIZE);
  customSearchURL.set(SIZE, String(size));
  customSearchURL.set(PAGE, '1');
  customSearchURL.set(START, '0');
  // Do not modify layout here; layout is managed separately (local state/localStorage) and URL should not override it.

  // Clear persisted filters for gallery scope (persist the "cleared" state)
  if (galleryId) {
    clearFilters(VIEW_CONTEXT.GALLERY as ViewContextScope, galleryId);
  }

  // Sort handling: preserve existing sort, else set from viewState if available, else leave unset
  if (!(preserveSort && existingSort)) {
    const { sortData } = viewStateVar();
    const sortVal = sortData?.sortBy?.value;
    const orderBy = sortData?.orderBy;
    if (sortVal) {
      customSearchURL.set(SORT, `${sortVal}:${orderBy}`);
    } else {
      // Intentionally omit SORT; SortBy will initialize default on render
      customSearchURL.delete(SORT);
    }
  }

  // Reset view state related to filters/search and selection; keep sortData intact if preserving
  viewStateVar({
    ...viewStateVar(),
    searchTerm: '',
    regularStartDate: '',
    regularEndDate: '',
    ISOStartDate: '',
    ISOEndDate: '',
    horizontalMediaCardDetails: { image: '', title: '' },
    akomiMediaCard: [],
    horizontalMediaCardIndex: 0,
    showHorizontal: false,
    filterChips: [],
    filterCategoryExclude: [],
    selectedAssets: [],
    isSelectAll: false,
    pagination: {
      ...viewStateVar().pagination,
      page: 1,
      cursor: null,
    },
    filter: {
      category: { id: '', name: '', label: '' },
      choices: [],
    },
  });
};
